import { useQueryClient } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import { Dropdown, Menu, Tooltip } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css, keyframes } from 'styled-components';

import {
  ArrowDownSvg,
  ArrowLeftSvg,
  CheckSvg,
  PlusSvg,
  QuestionMarkSvg,
} from '../../../../assets/icons';
import CuripodAIStarsSvg from '../../../../assets/icons/CuripodAIOrangeFilledSvg';
import CuripodAISolidStarsSvg from '../../../../assets/icons/CuripodAISolidStarsSvg';
import HomeFilledSvg from '../../../../assets/icons/HomeFilledSvg';
import { MagicBackground } from '../../../../assets/icons/MagicBackground';
import MediaIconSvg from '../../../../assets/icons/MediaIconSvg';
import PasteSvg from '../../../../assets/icons/PasteSvg';
import { ShuffleSvg } from '../../../../assets/icons/ShuffleSvg';
import UploadsSvg from '../../../../assets/icons/UploadsSvg';
import YoutubeSvg from '../../../../assets/icons/YoutubeSvg';
import theme from '../../../../assets/theme';
import GameAPI from '../../../../frontendServices/gameAPI';
import useAccessBadge from '../../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useDynamicLessonStateContext } from '../../../../hooks/useCreateLessonState';
import { buildInitialLessonPromptVariablesQueryKey } from '../../../../hooks/useDesignerUIState';
import { useLocale } from '../../../../hooks/useLocale';
import { useMetrics } from '../../../../hooks/useMetrics';
import { useNewPresentationId } from '../../../../hooks/useNewPresentationId';
import { usePrevious } from '../../../../hooks/usePrevious';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { useTestPrepState } from '../../../../hooks/useTestPrepState';
import { useUploadGenericFile } from '../../../../hooks/useUploadGenericFile';
import SocketProvider, {
  SocketContext,
} from '../../../../providers/SocketProvider/SocketProvider';
import { LLMMessageDTO } from '../../../../services/backendService/backendService';
import { FileUploads } from '../../../../services/backendService/types';
import { lessonCategories, writingSkills } from '../../../../utils/createLessonPageUtils';
import { createFileFromText } from '../../../../utils/fileUtils';
import { themeSlides } from '../../../../utils/slideThemingUtils';
import { Loader } from '../../../_atoms/_Loader';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { CustomIntercomButton } from '../../../_atoms/CustomIntercomButton';
import { HideNextJsLoader } from '../../../_atoms/HideNextJsLoader';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { LoadingDots } from '../../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import { ColorPalette } from '../../../_organisms/DesignerTabPane/AgentTab/AgentHooks';
import { Slide } from '../../../SlideComponents/slide.types';
import { CSSSlideWrapperViewer } from '../../../SlideComponents/SlideWrapperViewer';
import { CenteredContainer } from '../../../styled/CenteredContainer';
import { CategoryList } from '../CategorySelector';
import { SubPageLayout } from '../CreatePage';
import { CurifyTab } from '../CurifyTab';
import { FindCategorizedLessonsTab } from '../FindCategorizedLessonsTab';
import { useLearningObjectiveRefinement } from '../hooks/useLearningObjectiveRefinementHook';
import { MaterialAttachment, MaterialAttachments } from '../MaterialAttachments';
import { TestPrepTab } from '../TestPrepTab';
import { Page } from '../types';
import { UseMyTextModal } from '../UseMyTextModal';
import { ChatHistoryList } from './ChatHistoryList';

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const PreventPullToRefresh = () => {
  return (
    <style jsx global>{`
      /* prevent pull-to-refresh for Safari 16+ */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and (overscroll-behavior-y: none) {
          html {
            min-height: 100.3%;
            overscroll-behavior-y: none;
          }
        }
      }
      /* prevent pull-to-refresh for Safari 9~15 */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and
          (not (overscroll-behavior-y: none)) {
          html {
            height: 100%;
            overflow: hidden;
          }
          body {
            margin: 0px;
            max-height: 100%; /* or if body has default margin */
            overflow: auto;
            -webkit-overflow-scrolling: touch;
          }
          /* in this case to disable pinch-zoom, set  */
        }
      }

      /* prevent pull-to-refresh for Chrome 63+ */
      body {
        overscroll-behavior-y: none;
      }
    `}</style>
  );
};
// Mobile Components
const MobileInputContainer = styled.div<{
  $isObjectiveReady: boolean;
  $hasChatHistory: boolean;
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
  $isDisabled: boolean;
}>`
  width: 100%;
  margin: 0 auto;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.greyLight : theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;
  
  ${({ $hasChatHistory }) =>
    $hasChatHistory
      ? `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 5px 10px;
    `
      : `
    padding: 10px;
    `}

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
    ${({ $isObjectiveReady }) =>
      $isObjectiveReady &&
      `
    display: none;
  `}
  
`;

const MobileButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    justify-content: space-between;
    align-items: center;
    gap: 5px;
  }
`;

const MobileRightButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  width: fit-content;
  flex-wrap: wrap;
  align-items: center;
  margin-left: auto;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const MobileSubmitButton = styled(Button)`
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }

  @media (max-width: 768px) {
    height: 35px;
    width: 35px;
    padding: 18px;
    border-radius: 18px;
  }
`;

const MobileSubmitButtonFullWidth = styled(MobileSubmitButton)`
  width: 100%;
  height: 50px;
  border-radius: 25px;
  padding: 15px 20px;

  &:hover:not(:disabled) {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    height: 50px;
    border-radius: 25px;
    padding: 15px 20px;
  }
`;

const MobileLessonReadyButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileSuggestionsContainer = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  max-height: ${({ $isVisible }) => ($isVisible ? '500px' : '0')};
  transition: all 0.3s linear;
  overflow: hidden;
  padding: 0 15px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const MobileSuggestionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  align-items: center;
`;

const MobileSuggestionButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.greyVeryDark};
  padding: 10px 16px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 12px;
  background-color: rgba(251, 247, 242, 0.8);
  transition: all 0.2s ease;
  cursor: pointer;
  width: 100%;
  text-align: left;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }
`;

const LessonReadyButtons = styled.div`
  position: absolute;
  bottom: 0px;
  top: 10px;
  right: 0px;
  width: 100%;
  height: 100px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const GenerateLessonButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background: linear-gradient(45deg, #ffa55b, #ffc107, #ffa55b, #ffc107);
  background-size: 600% 400%;
  animation: ${gradientShift} 6s ease infinite;
  color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(45deg, #ffa55b, #ffc107);
  }

  &:active,
  &:focus {
    background: linear-gradient(45deg, #ffa55b, #ffc107);
  }

  svg {
    height: 25px;
    width: 25px;
  }
`;

const flexibleLessonSuggestions = [
  {
    summary: (
      <T keyName="summary_prompt_example_11" defaultValue="The Giver Novel Study" />
    ),
    promptKey: 'prompt_example_11',
    promptDefault:
      '8th grade novel study on The Giver by Lois Lowry, chapter 5. Focus on theme. 45 minutes.',
  },
  {
    summary: <T keyName="summary_prompt_example_12" defaultValue="Sensory Imagery" />,
    promptKey: 'prompt_example_12',
    promptDefault:
      '4th grade writing lesson. 30 minutes. Use sensory imagery to convey a sequence of events about an after-school hobby.',
  },
  {
    summary: <T keyName="summary_prompt_example_13" defaultValue="The American Dream:" />,
    promptKey: 'prompt_example_13',
    promptDefault:
      '11th grade ELA lesson. The Great Gatsby by F. Scott Fitzgerald. Focus on analyzing the American Dream. 50 minutes.',
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_14"
        defaultValue="Analyze Foreshadowing in Macbeth"
      />
    ),
    promptKey: 'prompt_example_14',
    promptDefault:
      '9th grade ELA. Macbeth Act III, Scene IV read aloud lesson. Analyze foreshadowing. 43 minutes.',
  },
  {
    summary: <T keyName="summary_prompt_example_15" defaultValue="Making Inferences" />,
    promptKey: 'prompt_example_15',
    promptDefault:
      '6th grade 20 minute read aloud lesson on The Hunger Games, chapter 7 focusing on making inferences.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_16" defaultValue="The Civil Rights Movement" />
    ),
    promptKey: 'prompt_example_16',
    promptDefault:
      '10th grade U.S. History lesson. The Civil Rights Movement. Analyze the strategies of leaders like Martin Luther King Jr. 30 minutes.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_17" defaultValue="Ancient Civilizations" />
    ),
    promptKey: 'prompt_example_17',
    promptDefault:
      'How geography influenced the development of the ancient Mayan city of Tikal for 6th graders. I have 32 minutes.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_18" defaultValue="The Battle of the Alamo" />
    ),
    promptKey: 'prompt_example_18',
    promptDefault: '25 minutes lesson on The Battle of the Alamo for 4th graders.',
  },
  {
    summary: <T keyName="summary_prompt_example_19" defaultValue="Writing Practice" />,
    promptKey: 'prompt_example_19',
    promptDefault: 'Practice hyperbole in writing in 10 minutes for 7th grade.',
  },
  {
    summary: <T keyName="summary_prompt_example_20" defaultValue="Figurative Language" />,
    promptKey: 'prompt_example_20',
    promptDefault:
      'Analyze examples of figurative language. 15 minute lesson for 9th graders.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_21" defaultValue="Describe Quadrilaterals" />
    ),
    promptKey: 'prompt_example_21',
    promptDefault:
      '5th math; practice using attributes to classify quadrilaterals; 15 minutes',
  },
  {
    summary: <T keyName="summary_prompt_example_22" defaultValue="Ratios in the NBA" />,
    promptKey: 'prompt_example_22',
    promptDefault:
      '20 min; identify and represent equivalent ratios in NBA stats; 6th grade',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_23" defaultValue="Two-step Linear Equations" />
    ),
    promptKey: 'prompt_example_23',
    promptDefault:
      'Practice solving linear equations in the form p(x+q)=y for 7th grade math; I have 23 minutes',
  },
  {
    summary: <T keyName="summary_prompt_example_24" defaultValue="Grouping by Tens" />,
    promptKey: 'prompt_example_24',
    promptDefault: '50 minutes on adding and subtracting ten within 100. 1st grade.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_25" defaultValue="Area Model Multiplication" />
    ),
    promptKey: 'prompt_example_25',
    promptDefault:
      '25 minute lesson on multiplying 2-digit by 2-digit numbers using the area model; 3rd grade.',
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_26"
        defaultValue="Relative Frequency of Going Viral"
      />
    ),
    promptKey: 'prompt_example_26',
    promptDefault:
      'Use conditional relative frequencies to find which variable shows the strongest association with Going viral on Tik Tok. 8th math. 27 minutes.',
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_27"
        defaultValue="Energy Flow in the Texas Hill Country"
      />
    ),
    promptKey: 'prompt_example_27',
    promptDefault:
      '7th grade. Use the 10% rule to model energy flow across trophic levels in the Texas hill country.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_28" defaultValue="Conservation of Matter" />
    ),
    promptKey: 'prompt_example_28',
    promptDefault:
      '25 min; use the law of conservation of matter to identify balanced and unbalanced chemical equations. 8th grade.',
  },
  {
    summary: <T keyName="summary_prompt_example_29" defaultValue="Lab Safety" />,
    promptKey: 'prompt_example_29',
    promptDefault:
      '10 minute orientation to lab safety for chemical experiments; 9th graders',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_30" defaultValue="Modeling the Rock Cycle" />
    ),
    promptKey: 'prompt_example_30',
    promptDefault:
      'Model the rock cycle, labeling the rock attributes and type of change; 35 minutes; 3rd grade',
  },
];

// Default lesson suggestions - store translation keys instead of translated values
const defaultLessonSuggestions = [
  {
    summary: (
      <T keyName="summary_prompt_example_1" defaultValue="The Giver Novel Study" />
    ),
    promptKey: 'prompt_example_1',
    promptDefault:
      '8th grade novel study on The Giver by Lois Lowry, chapter 5. Focus on theme development.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_2" defaultValue="Things Fall Apart Ch. 13" />
    ),
    promptKey: 'prompt_example_2',
    promptDefault:
      'Novel study lesson for 10th graders. Things Fall Apart by Chinua Achebe, chapter 13, with a focus on plot development.',
  },
  {
    summary: (
      <T keyName="summary_prompt_example_3" defaultValue="Macbeth Act III, Scene IV" />
    ),
    promptKey: 'prompt_example_3',
    promptDefault:
      'Macbeth Act III, Scene IV read aloud lesson. Analyze foreshadowing. 9th grade ELA.',
  },
  {
    summary: <T keyName="summary_prompt_example_4" defaultValue="Holes Read Aloud" />,
    promptKey: 'prompt_example_4',
    promptDefault: '4th grade read aloud lesson: Holes chapter 30, focus on conflict.',
  },
  {
    summary: <T keyName="summary_prompt_example_5" defaultValue="Making inferences" />,
    promptKey: 'prompt_example_5',
    promptDefault:
      '6th grade novel study on The Giver Chapter 3 focusing on making inferences.',
  },
  {
    summary: <T keyName="summary_prompt_example_6" defaultValue="Sensory imagery" />,
    promptKey: 'prompt_example_6',
    promptDefault:
      '4th grade writing lesson. Use sensory imagery to convey a sequence of events from summer break.',
  },
] as const;

const ObjectiveRefinementWithSocket = () => {
  useLocale();
  const dispatchAndSync = useDynamicLessonStateContext(
    context => context.dispatchAndSync,
  );
  const agentHistory = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.agentHistory,
  );
  const { t } = useTranslate();
  const { tenantId, router } = useRouterMatch();
  const [inputValue, setInputValue] = useState('');
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [isInputContainerHovered, setIsInputContainerHovered] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const hasProcessedUrlPrompt = useRef(false);
  const metrics = useMetrics();
  const client = useQueryClient();
  const nextRouter = useRouter();
  const isDesktop = useBreakPoint(768);
  const [chatSubmissionCounter, setChatSubmissionCounter] = useState(0);

  const isStreaming = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isStreaming,
  );
  const wasStreamingOnPreviousRender = usePrevious(isStreaming);
  useEffect(() => {
    if (wasStreamingOnPreviousRender && !isStreaming) {
      inputRef.current?.focus();
    }
  }, [wasStreamingOnPreviousRender, isStreaming]);

  const promptVariables = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.promptVariables,
  );

  const llmThreadId = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.llmThreadId,
  );

  const isObjectiveReady = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isObjectiveReady,
  );
  const isComplete = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isComplete,
  );

  const isFlexibleLessonOneFlagEnabled = useFlag('flexible-lesson-one');

  const { gameAPI } = useContext(SocketContext);

  // File state
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<Array<MaterialAttachment>>([]);

  const { startStream, stopStream } = useLearningObjectiveRefinement();

  const isConnected = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isConnected,
  );

  const submitPrompt = useCallback(
    (prompt: string, messages: LLMMessageDTO[] = []) => {
      // Add user message to chat history immediately
      const userMessage: LLMMessageDTO = {
        role: 'USER',
        content: prompt,
        createdAt: new Date().toISOString(),
        id: 'unknown',
        model: 'o3',
        modifiedAt: new Date().toISOString(),
        threadId: 'unknown',
        attachments: attachments.map(
          a =>
            (({
              id: a.fileUploadId,
              fileId: a.fileId,
              mimeType: a.mimetype,
              fileName: a.fileName,
            } as unknown) as FileUploads),
        ) as FileUploads[],
      };
      dispatchAndSync({
        type: 'SET_AGENT_HISTORY',
        payload: [...(messages || []), userMessage],
      });
      streamCleanupRef.current?.();

      // Start the stream with just the message
      const cleanup = startStream({
        historyLength: messages.length,
        message: prompt,
        attachments: attachments.map(a => ({ fileUploadId: a.fileUploadId })),
      });
      streamCleanupRef.current = cleanup || null;
    },
    [attachments, dispatchAndSync, startStream],
  );

  const handleSubmit = () => {
    if (!inputValue.trim()) return;
    submitPrompt(inputValue, agentHistory);
    setInputValue('');
    setAttachments([]);
    setChatSubmissionCounter(prev => prev + 1);
  };

  // if being redirected from landing page, auto-submit the prompt
  useEffect(() => {
    if (
      nextRouter.query.prompt &&
      typeof nextRouter.query.prompt === 'string' &&
      agentHistory.length === 0 &&
      !hasProcessedUrlPrompt.current
    ) {
      hasProcessedUrlPrompt.current = true;
      // Clear the URL parameter immediately to prevent re-triggering
      nextRouter.replace(`/${tenantId}/create`, undefined, { shallow: true });

      submitPrompt(nextRouter.query.prompt);
      setChatSubmissionCounter(prev => prev + 1);
    }
  }, [submitPrompt, nextRouter, tenantId, agentHistory]);

  const {
    createPresentationId,
    isLoading: isCreatingPresentationId,
  } = useNewPresentationId();

  // Randomly select 3 suggestions
  const [randomSuggestions, setRandomSuggestions] = useState(() => {
    if (isFlexibleLessonOneFlagEnabled) {
      const shuffled = [...flexibleLessonSuggestions].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 3);
    }
    const shuffled = [...defaultLessonSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  });

  const shuffleSuggestions = () => {
    if (isFlexibleLessonOneFlagEnabled) {
      const shuffled = [...flexibleLessonSuggestions].sort(() => 0.5 - Math.random());
      setRandomSuggestions(shuffled.slice(0, 3));
      return;
    }
    const shuffled = [...defaultLessonSuggestions].sort(() => 0.5 - Math.random());
    setRandomSuggestions(shuffled.slice(0, 3));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  const handleSuggestionClick = (fullValue: string) => {
    setInputValue(fullValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleGenerateLesson = () => {
    if (!promptVariables) {
      return;
    }

    createPresentationId({
      onSuccess: presentationId => {
        client.setQueryData(
          buildInitialLessonPromptVariablesQueryKey(presentationId),
          promptVariables,
        );

        // Moved it here to we can capture the presentationId
        metrics.logEvent('CreatePage.ObjectiveRefinement.GenerateLessonClicked', {
          chatHistoryLength: agentHistory.length,
          sessionLength: 0,
          isObjectiveReady,
          ...promptVariables,
          tenantId,
          attachments: (promptVariables.attachments ?? []).map(a => ({
            fileUploadId: a.fileUploadId,
          })),
          presentationId,
          llmThreadId: llmThreadId,
        });

        if (isFlexibleLessonOneFlagEnabled && gameAPI) {
          // move to theme picker tab and start lesson generation under the hood
          gameAPI.getSocket().emit(
            'build:lesson',
            {
              presentationId,
              message: 'Generate lesson',
              ...promptVariables, //
            },
            (_: {
              status: number;
              data: { slides: Slide[]; messages: LLMMessageDTO[] };
              error?: { status: number; message: string };
            }) => {
              dispatchAndSync({
                type: 'SET_COLOR_PALETTE_PICKER',
                payload: { presentationId },
              });
            },
          );
        } else {
          router.push(
            `/${tenantId}/lessons/${presentationId}/edit?buildInitialLesson=true`,
          );
        }
      },
    });
  };
  const { upload: uploadGenericFile, isUploading } = useUploadGenericFile(tenantId);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData('text/plain');
    e.preventDefault();

    // If chat has started, always insert inline (no attachments)
    const shouldInsertInline = agentHistory.length >= 1 || true; //text.length < 100;
    if (shouldInsertInline) {
      // Insert text at cursor position instead of replacing entire value
      const textArea = e.target as HTMLTextAreaElement;
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      const currentValue = textArea.value;
      const newValue =
        currentValue.substring(0, start) + text + currentValue.substring(end);

      setInputValue(newValue);
      return;
    }
    // Otherwise we make a txt file.

    // Create a .txt file from the text
    const file = createFileFromText(text);
    uploadGenericFile(
      { file, fileType: 'materialAttachment' },
      {
        onSuccess: result => {
          setAttachments(prev => [...prev, result]);
        },
        onError: error => {
          // TODO: display to user
        },
      },
    );
  };

  if (!isDesktop) {
    return (
      <ContainerMobile $isChatHistory={agentHistory.length >= 1}>
        <PreventPullToRefresh />

        <Title $hasHistory={agentHistory.length >= 1}>
          <T
            keyName="ObjectiveRefinement.Title"
            defaultValue="What will you teach today?"
          />
        </Title>

        {agentHistory.length >= 1 && (
          <ChatHistoryList onChatSubmitted={chatSubmissionCounter} />
        )}

        <MobileFormContainer
          $hasChatHistory={agentHistory.length >= 1}
          $isObjectiveReady={isObjectiveReady}
        >
          <MobileInputContainer
            $isObjectiveReady={isObjectiveReady}
            $isDisabled={isStreaming}
            $hasChatHistory={agentHistory.length >= 1}
            $isTextAreaFocused={isTextAreaFocused}
            $isHovered={isInputContainerHovered}
            onMouseEnter={() => setIsInputContainerHovered(true)}
            onMouseLeave={() => setIsInputContainerHovered(false)}
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          >
            <TextArea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onFocus={() => setIsTextAreaFocused(true)}
              onBlur={() => setIsTextAreaFocused(false)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              fluid={true}
              placeholder={
                !isConnected && agentHistory.length > 0
                  ? t('Connecting to server...', 'Connecting to server...')
                  : agentHistory.length === 0 && !isStreaming
                  ? t(
                      'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                      'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                    )
                  : isStreaming || isObjectiveReady
                  ? '' // Empty when streaming to show animated overlay
                  : t('Reply to Curipod AI...', 'Reply to Curipod AI...')
              }
              autoSize={{
                minRows: agentHistory.length > 0 ? 1 : 3,
                maxRows: agentHistory.length > 0 ? 4 : 8,
              }}
              ref={inputRef}
              autoFocus
              disabled={isStreaming || isObjectiveReady}
              textAreaStyled={{
                fontSize: 16,
                fontWeight: 600,
                padding: '15px',
                borderRadius: 15,
                border: 'none',
                backgroundColor: 'transparent',
              }}
              style={{
                width: '100%',
              }}
            />

            {isStreaming && (
              <MobileLoadingOverlay>
                <T keyName="Generating" defaultValue="Generating" />
                <LoadingDots>...</LoadingDots>
              </MobileLoadingOverlay>
            )}

            {!agentHistory.length && (
              <MobileButtonsBar>
                <Dropdown
                  overlay={
                    <Menu>
                      <StyledMenuItem
                        key="text"
                        onClick={() => {
                          metrics.logEvent(
                            'CreatePage.UseMyMaterials.SelectPasteInTextOption',
                            {
                              promptInput: inputValue,
                            },
                          );
                          setShowPasteModal(true);
                        }}
                      >
                        <IconWrapper height="16px" width="16px">
                          <PasteSvg />
                        </IconWrapper>
                        <span>
                          <T keyName="Paste in text" defaultValue="Paste in text" />
                        </span>
                      </StyledMenuItem>
                      <StyledMenuItem
                        key="presentation"
                        onClick={() => {
                          metrics.logEvent('CreatePage.SelectPresentation', {
                            promptInput: inputValue,
                          });
                          dispatchAndSync({
                            type: 'SET_MAIN_LESSON_CATEGORY',
                            payload: 'upload',
                          });
                        }}
                      >
                        <IconWrapper height="16px" width="16px">
                          <MediaIconSvg />
                        </IconWrapper>
                        <span>
                          <T
                            keyName="Presentation (PPT, PDF, Google)"
                            defaultValue="Presentation (PPT, PDF, Google)"
                          />
                        </span>
                      </StyledMenuItem>
                      <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                        <StyledMenuItem
                          key="youtube"
                          onClick={() => {
                            // Do nothing - disabled item
                          }}
                          style={{ opacity: 0.5, cursor: 'not-allowed' }}
                        >
                          <IconWrapper height="16px" width="16px">
                            <YoutubeSvg />
                          </IconWrapper>
                          <span>
                            <T keyName="Youtube link" defaultValue="Youtube link" />
                          </span>
                        </StyledMenuItem>
                      </Tooltip>
                      <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                        <StyledMenuItem
                          key="upload"
                          onClick={() => {
                            // Do nothing - disabled item
                            // hiddenFileInputRef.current?.click();
                          }}
                          style={{ opacity: 0.5, cursor: 'not-allowed' }}
                        >
                          <IconWrapper height="16px" width="16px">
                            <UploadsSvg />
                          </IconWrapper>
                          <span>
                            <T keyName="Upload file" defaultValue="Upload file" />
                          </span>
                          <input
                            ref={hiddenFileInputRef}
                            type="file"
                            name="myFile"
                            multiple={false}
                            accept="image/jpeg,image/png"
                            // onChange={onFileSelected}
                            style={{ display: 'none' }}
                          />
                        </StyledMenuItem>
                      </Tooltip>
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <AddResourcesDropdownButton
                    type="ghost"
                    onClick={e => e.stopPropagation()}
                  >
                    <span className="button-text">
                      <T keyName="Use my materials" defaultValue="Use my materials" />
                    </span>
                    <IconWrapper height="15px" width="15px">
                      <PlusSvg />
                    </IconWrapper>
                  </AddResourcesDropdownButton>
                </Dropdown>

                <MobileRightButtonsContainer onClick={e => e.stopPropagation()}>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          style={{
                            background: theme.colors.lightestGreen,
                            color: theme.colors.black,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}
                          key="fullLesson"
                          disabled={true}
                        >
                          <span
                            style={{
                              width: '100%',
                              minWidth: '200px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                            }}
                          >
                            <T keyName="Full Lesson" defaultValue="Full Lesson" />
                            <CheckSvg />
                          </span>
                        </Menu.Item>
                        <Menu.Item
                          key="quickWrites"
                          onClick={() => {
                            metrics.logEvent('CreatePage.SelectQuickWrites', {
                              promptInput: inputValue,
                            });
                            dispatchAndSync({
                              type: 'SET_MAIN_LESSON_CATEGORY',
                              payload: 'writing_skills',
                            });
                          }}
                        >
                          <T keyName="Quick Writes" defaultValue="Quick Writes" />
                        </Menu.Item>
                        <Menu.Item
                          key="testPrep"
                          onClick={() => {
                            metrics.logEvent('CreatePage.SelectTestPrep', {
                              promptInput: inputValue,
                            });
                            dispatchAndSync({
                              type: 'SET_MAIN_LESSON_CATEGORY',
                              payload: 'test_prep',
                            });
                          }}
                        >
                          <T keyName="Test Prep" defaultValue="Test Prep" />
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                  >
                    <LessonTypeDropdownButton
                      type="ghost"
                      onClick={e => e.stopPropagation()}
                    >
                      <T keyName="Full lesson" defaultValue="Full lesson" />
                      <IconWrapper
                        style={{ transform: 'rotate(-90deg)' }}
                        height="16px"
                        width="16px"
                      >
                        <ArrowLeftSvg />
                      </IconWrapper>
                    </LessonTypeDropdownButton>
                  </Dropdown>

                  <MobileSubmitButton
                    onClick={e => {
                      e.stopPropagation();
                      handleSubmit();
                    }}
                    disabled={!inputValue.trim() || isStreaming || !isConnected}
                  >
                    <IconWrapper
                      style={{ transform: 'rotate(-90deg)' }}
                      height="20px"
                      width="20px"
                      color={theme.colors.white}
                    >
                      <ArrowDownSvg />
                    </IconWrapper>
                  </MobileSubmitButton>
                </MobileRightButtonsContainer>
              </MobileButtonsBar>
            )}

            {(isUploading || attachments.length > 0) && (
              <MaterialAttachments
                isUploading={isUploading}
                attachments={attachments}
                removeAttachment={(fileId: string) => {
                  setAttachments(prev => prev.filter(a => a.fileId !== fileId));
                }}
              />
            )}

            {agentHistory.length >= 1 && !isStreaming && !isObjectiveReady && (
              <MobileSubmitButtonWrapper>
                <MobileSubmitButton
                  onClick={handleSubmit}
                  disabled={!inputValue.trim() || isStreaming || !isConnected}
                >
                  <IconWrapper
                    style={{ transform: 'rotate(180deg)' }}
                    height="20px"
                    width="20px"
                    color={theme.colors.white}
                  >
                    <ArrowDownSvg />
                  </IconWrapper>
                </MobileSubmitButton>
              </MobileSubmitButtonWrapper>
            )}

            {isStreaming && (
              <MobileSubmitButtonWrapper>
                <MobileSubmitButton
                  onClick={() => stopStream()}
                  styles={{
                    background: theme.colors.greyLight,
                    border: `1px solid ${theme.colors.grey}`,
                  }}
                >
                  <IconWrapper height="20px" width="20px" color={theme.colors.greyDark}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 14 14"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                        fill="currentColor"
                      />
                    </svg>
                  </IconWrapper>
                </MobileSubmitButton>
              </MobileSubmitButtonWrapper>
            )}
          </MobileInputContainer>

          {agentHistory.length === 0 && (
            <SuggestionsWrapper>
              <MobileSuggestionsContainer $isVisible={showSuggestions}>
                <MobileSuggestionsGrid>
                  {randomSuggestions.map((suggestion, index) => (
                    <MobileSuggestionButton
                      key={index}
                      onClick={() => {
                        metrics.logEvent('CreatePage.SelectLessonIdea', {
                          lessonIdea: t(suggestion.promptKey, suggestion.promptDefault),
                        });
                        handleSuggestionClick(
                          t(suggestion.promptKey, suggestion.promptDefault),
                        );
                      }}
                    >
                      {suggestion.summary}
                    </MobileSuggestionButton>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '2px',
                    }}
                  >
                    <ShuffleButton
                      onClick={shuffleSuggestions}
                      title={t('Get new suggestions', 'Get new suggestions')}
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        color={theme.colors.greyDark}
                      >
                        <ShuffleSvg />
                      </IconWrapper>
                    </ShuffleButton>
                  </div>
                </MobileSuggestionsGrid>
              </MobileSuggestionsContainer>

              <CollapseButton
                type="linkStyle"
                onClick={() => {
                  if (!showSuggestions) {
                    metrics.logEvent('CreatePage.ShowLessonIdeas');
                    setShowSuggestions(true);
                  } else {
                    metrics.logEvent('CreatePage.HideLessonIdeas');
                    setShowSuggestions(false);
                  }
                }}
              >
                {showSuggestions ? (
                  <T keyName="Hide Lesson Ideas" defaultValue="Hide Lesson Ideas" />
                ) : (
                  <T keyName="Show Lesson Ideas" defaultValue="Show Lesson Ideas" />
                )}
              </CollapseButton>
            </SuggestionsWrapper>
          )}

          {/* Mobile lesson ready buttons - fixed position */}
          {isObjectiveReady && agentHistory.length > 0 && !isStreaming && isComplete && (
            <MobileLessonReadyButtons>
              <Button
                type="ghost"
                onClick={() => {
                  // Track when user clicks "Keep chatting" instead of generating lesson
                  metrics.logEvent('CreatePage.ObjectiveRefinement.KeepChattingClicked', {
                    chatHistoryLength: agentHistory.length,
                    sessionLength: 0,
                    isObjectiveReady: isObjectiveReady,
                    promptVariables: promptVariables
                      ? {
                          learningObjective: promptVariables.learningObjective,
                          grade: promptVariables.grade,
                          subject: promptVariables.subject,
                          languages: promptVariables.languages,
                          lessonCategory: promptVariables.lessonCategory,
                          standardAlignment: promptVariables.standardAlignment,
                        }
                      : null,
                  });

                  // Focus back on the input to continue chatting
                  if (inputRef.current) {
                    dispatchAndSync({
                      type: 'SET_AGENT_KEEP_EDITING',
                    });
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 100);
                  }
                }}
                styles={{
                  width: '100%',
                  height: '50px',
                  borderRadius: '25px',
                  backgroundColor: theme.colors.white,
                  border: `1px solid ${theme.colors.grey}`,
                }}
              >
                <T keyName="Keep chatting" defaultValue="Keep chatting" />
              </Button>
              <GenerateLessonButton
                onClick={handleGenerateLesson}
                loading={isCreatingPresentationId}
                icon={
                  <IconWrapper height="20px" width="20px">
                    <CuripodAISolidStarsSvg />
                  </IconWrapper>
                }
                styles={{
                  width: '100%',
                  height: '50px',
                  borderRadius: '25px',
                }}
              >
                <T keyName="Generate lesson" defaultValue="Generate lesson" />
              </GenerateLessonButton>
            </MobileLessonReadyButtons>
          )}
        </MobileFormContainer>

        <UseMyTextModal
          tenantId={tenantId}
          isVisible={showPasteModal}
          onCancel={() => setShowPasteModal(false)}
          onFileUploadSuccess={file => {
            setAttachments(prev => [...prev, file]);
          }}
        />
      </ContainerMobile>
    );
  }

  // Desktop version
  return (
    <Container $isChatHistory={agentHistory.length >= 1}>
      <Title $hasHistory={agentHistory.length >= 1}>
        <T
          keyName="ObjectiveRefinement.Title"
          defaultValue="What will you teach today?"
        />
      </Title>

      <ChatHistoryList onChatSubmitted={chatSubmissionCounter} />

      <FormContainer
        style={{
          maxHeight: agentHistory.length >= 1 ? 'fit-content' : 'unset',
          paddingBottom: agentHistory.length >= 1 ? '10px' : '20px',
        }}
      >
        <InputContainer
          $isDisabled={isStreaming}
          $hasChatHistory={agentHistory.length >= 1}
          $isTextAreaFocused={isTextAreaFocused}
          $isHovered={isInputContainerHovered}
          onMouseEnter={() => setIsInputContainerHovered(true)}
          onMouseLeave={() => setIsInputContainerHovered(false)}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <TextArea
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={() => setIsTextAreaFocused(true)}
            onBlur={() => setIsTextAreaFocused(false)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={
              !isConnected && agentHistory.length > 0
                ? t('Connecting to server...', 'Connecting to server...')
                : agentHistory.length === 0 && !isStreaming
                ? t(
                    'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                    'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                  )
                : isStreaming || isObjectiveReady
                ? '' // Empty when streaming to show animated overlay
                : t('Reply to Curipod AI...', 'Reply to Curipod AI...')
            }
            autoSize={{ minRows: 2, maxRows: 10 }}
            ref={inputRef}
            autoFocus
            disabled={isStreaming || isObjectiveReady}
            textAreaStyled={{
              fontSize: 18,
              fontWeight: 600,
              padding: '10px',
              borderRadius: 0,
              border: 'none',
              backgroundColor: 'transparent',
            }}
            style={{
              minWidth: '100%',
            }}
          />

          {isStreaming && (
            <LoadingOverlay>
              <T keyName="Generating" defaultValue="Generating" />
              <LoadingDots>...</LoadingDots>
            </LoadingOverlay>
          )}

          {!agentHistory.length && (
            <ButtonsBar>
              <Dropdown
                overlay={
                  <Menu>
                    <StyledMenuItem
                      key="text"
                      onClick={() => {
                        metrics.logEvent(
                          'CreatePage.UseMyMaterials.SelectPasteInTextOption',
                          {
                            promptInput: inputValue,
                          },
                        );
                        setShowPasteModal(true);
                      }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <PasteSvg />
                      </IconWrapper>
                      <span>
                        <T keyName="Paste in text" defaultValue="Paste in text" />
                      </span>
                    </StyledMenuItem>
                    <StyledMenuItem
                      key="presentation"
                      onClick={() => {
                        metrics.logEvent('CreatePage.SelectPresentation', {
                          promptInput: inputValue,
                        });
                        dispatchAndSync({
                          type: 'SET_MAIN_LESSON_CATEGORY',
                          payload: 'upload',
                        });
                      }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <MediaIconSvg />
                      </IconWrapper>
                      <span>
                        <T
                          keyName="Presentation (PPT, PDF, Google)"
                          defaultValue="Presentation (PPT, PDF, Google)"
                        />
                      </span>
                    </StyledMenuItem>
                    <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                      <StyledMenuItem
                        key="youtube"
                        onClick={() => {
                          // Do nothing - disabled item
                        }}
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                      >
                        <IconWrapper height="16px" width="16px">
                          <YoutubeSvg />
                        </IconWrapper>
                        <span>
                          <T keyName="Youtube link" defaultValue="Youtube link" />
                        </span>
                      </StyledMenuItem>
                    </Tooltip>
                    <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                      <StyledMenuItem
                        key="upload"
                        onClick={() => {
                          // Do nothing - disabled item
                          // hiddenFileInputRef.current?.click();
                        }}
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                      >
                        <IconWrapper height="16px" width="16px">
                          <UploadsSvg />
                        </IconWrapper>
                        <span>
                          <T keyName="Upload file" defaultValue="Upload file" />
                        </span>
                        <input
                          ref={hiddenFileInputRef}
                          type="file"
                          name="myFile"
                          multiple={false}
                          accept="image/jpeg,image/png"
                          // onChange={onFileSelected}
                          style={{ display: 'none' }}
                        />
                      </StyledMenuItem>
                    </Tooltip>
                  </Menu>
                }
                trigger={['click']}
              >
                <AddResourcesDropdownButton type="ghost">
                  <T keyName="Use my materials" defaultValue="Use my materials" />
                  <IconWrapper height="15px" width="15px">
                    <PlusSvg />
                  </IconWrapper>
                </AddResourcesDropdownButton>
              </Dropdown>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  width: 'fit-content',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  marginLeft: 'auto',
                }}
              >
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        style={{
                          background: theme.colors.lightestGreen,
                          color: theme.colors.black,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                        }}
                        key="fullLesson"
                        disabled={true}
                      >
                        <span
                          style={{
                            width: '100%',
                            minWidth: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}
                        >
                          <T keyName="Full Lesson" defaultValue="Full Lesson" />
                          <CheckSvg />
                        </span>
                      </Menu.Item>
                      <Menu.Item
                        key="quickWrites"
                        onClick={() => {
                          metrics.logEvent('CreatePage.SelectQuickWrites', {
                            promptInput: inputValue,
                          });
                          dispatchAndSync({
                            type: 'SET_MAIN_LESSON_CATEGORY',
                            payload: 'writing_skills',
                          });
                        }}
                      >
                        <T keyName="Quick Writes" defaultValue="Quick Writes" />
                      </Menu.Item>
                      <Menu.Item
                        key="testPrep"
                        onClick={() => {
                          metrics.logEvent('CreatePage.SelectTestPrep', {
                            promptInput: inputValue,
                          });
                          dispatchAndSync({
                            type: 'SET_MAIN_LESSON_CATEGORY',
                            payload: 'test_prep',
                          });
                        }}
                      >
                        <T keyName="Test Prep" defaultValue="Test Prep" />
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <LessonTypeDropdownButton type="ghost">
                    <T keyName="Full lesson" defaultValue="Full lesson" />{' '}
                    <IconWrapper
                      style={{ transform: 'rotate(-90deg)' }}
                      height="16px"
                      width="16px"
                    >
                      <ArrowLeftSvg />
                    </IconWrapper>
                  </LessonTypeDropdownButton>
                </Dropdown>

                <SubmitButton
                  onClick={handleSubmit}
                  disabled={!inputValue.trim() || isStreaming || !isConnected}
                >
                  <IconWrapper
                    style={{ transform: 'rotate(-90deg)' }}
                    height="20px"
                    width="20px"
                    color={theme.colors.white}
                  >
                    <ArrowDownSvg />
                  </IconWrapper>
                </SubmitButton>
              </div>
            </ButtonsBar>
          )}

          {(isUploading || attachments.length > 0) && (
            <MaterialAttachments
              isUploading={isUploading}
              attachments={attachments}
              removeAttachment={(fileId: string) => {
                setAttachments(prev => prev.filter(a => a.fileId !== fileId));
              }}
            />
          )}
          {agentHistory.length >= 1 && !isStreaming && (
            <SubmitButton
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isStreaming || !isConnected}
              styles={{
                marginLeft: 'auto',
                visibility: isObjectiveReady ? 'hidden' : 'visible',
              }}
            >
              <IconWrapper
                style={{ transform: 'rotate(180deg)' }}
                height="20px"
                width="20px"
                color={theme.colors.white}
              >
                <ArrowDownSvg />
              </IconWrapper>
            </SubmitButton>
          )}
          {isStreaming && (
            <Button
              type="ghost"
              onClick={() => stopStream()}
              icon={
                <IconWrapper height="14px" width="14px" color={theme.colors.greyDark}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                      fill="currentColor"
                    />
                  </svg>
                </IconWrapper>
              }
              styles={{
                marginLeft: 'auto',
                width: 'fit-content',
                borderRadius: '20px',
                height: '40px',
              }}
            >
              <T keyName="Stop" defaultValue="Stop" />
            </Button>
          )}
          {/* Show Generate Lesson options when objective is ready */}
          {isObjectiveReady && agentHistory.length > 0 && !isStreaming && isComplete && (
            <LessonReadyButtons>
              <Button
                type="ghost"
                onClick={() => {
                  // Track when user clicks "Keep chatting" instead of generating lesson
                  metrics.logEvent('CreatePage.ObjectiveRefinement.KeepChattingClicked', {
                    chatHistoryLength: agentHistory.length,
                    sessionLength: 0,
                    isObjectiveReady: isObjectiveReady,
                    promptVariables: promptVariables
                      ? {
                          learningObjective: promptVariables.learningObjective,
                          grade: promptVariables.grade,
                          subject: promptVariables.subject,
                          languages: promptVariables.languages,
                          lessonCategory: promptVariables.lessonCategory,
                          standardAlignment: promptVariables.standardAlignment,
                        }
                      : null,
                  });

                  // Focus back on the input to continue chatting
                  if (inputRef.current) {
                    dispatchAndSync({
                      type: 'SET_AGENT_KEEP_EDITING',
                    });
                    // :grimacing_emoji:
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 100);
                  }
                }}
              >
                <T keyName="Keep chatting" defaultValue="Keep chatting" />
              </Button>
              <GenerateLessonButton
                onClick={handleGenerateLesson}
                loading={isCreatingPresentationId}
                icon={
                  <IconWrapper height="20px" width="20px">
                    <CuripodAISolidStarsSvg />
                  </IconWrapper>
                }
              >
                <T keyName="Generate lesson" defaultValue="Generate lesson" />
              </GenerateLessonButton>
            </LessonReadyButtons>
          )}
        </InputContainer>
        {agentHistory.length === 0 && (
          <SuggestionsWrapper>
            <SuggestionsContainer $isVisible={showSuggestions}>
              <SuggestionsGrid>
                {randomSuggestions.map((suggestion, index) => (
                  <SuggestionButton
                    key={index}
                    onClick={() => {
                      metrics.logEvent('CreatePage.SelectLessonIdea', {
                        lessonIdea: t(suggestion.promptKey, suggestion.promptDefault),
                      });
                      handleSuggestionClick(
                        t(suggestion.promptKey, suggestion.promptDefault),
                      );
                    }}
                  >
                    {suggestion.summary}
                  </SuggestionButton>
                ))}
                <ShuffleButton
                  onClick={shuffleSuggestions}
                  title={t('Get new suggestions', 'Get new suggestions')}
                >
                  <IconWrapper height="16px" width="16px" color={theme.colors.greyDark}>
                    <ShuffleSvg />
                  </IconWrapper>
                </ShuffleButton>
              </SuggestionsGrid>
            </SuggestionsContainer>

            <CollapseButton
              type="linkStyle"
              onClick={() => {
                if (!showSuggestions) {
                  metrics.logEvent('CreatePage.ShowLessonIdeas');
                  setShowSuggestions(true);
                } else {
                  metrics.logEvent('CreatePage.HideLessonIdeas');
                  setShowSuggestions(false);
                }
              }}
            >
              {showSuggestions ? (
                <T keyName="Hide Lesson Ideas" defaultValue="Hide Lesson Ideas" />
              ) : (
                <T keyName="Show Lesson Ideas" defaultValue="Show Lesson Ideas" />
              )}
            </CollapseButton>
          </SuggestionsWrapper>
        )}
      </FormContainer>

      <UseMyTextModal
        tenantId={tenantId}
        isVisible={showPasteModal}
        onCancel={() => setShowPasteModal(false)}
        onFileUploadSuccess={file => {
          setAttachments(prev => [...prev, file]);
        }}
      />
    </Container>
  );
};

const Container = styled.div<{ $isChatHistory: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${({ $isChatHistory }) => ($isChatHistory ? '0px' : '20vh')};
  height: 100vh;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  transition: all 0.8s ease-in-out;
  position: relative;
  z-index: 2;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: #fbf7f2;
    background: linear-gradient(
      0deg,
      rgba(251, 247, 242, 0) 0%,
      rgba(251, 247, 242, 1) 100%
    );
  }
`;

const ContainerMobile = styled.div<{ $isChatHistory: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${({ $isChatHistory }) => ($isChatHistory ? '30px' : '20vh')};
  min-height: 100dvh;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  transition: all 0.8s ease-in-out;
  position: relative;
  z-index: 2;

  //ChatHistory scroll fix
  ${({ $isChatHistory }) =>
    $isChatHistory &&
    `
    overflow: hidden;
    height: 100dvh;
    touch-action: none;
    position: fixed;
  `}

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: #fbf7f2;
    background: linear-gradient(
      0deg,
      rgba(251, 247, 242, 0) 0%,
      rgba(251, 247, 242, 1) 100%
    );
  }
`;

const Title = styled.h1<{ $hasHistory: boolean }>`
  font-size: 48px;
  font-weight: 600;
  color: ${theme.colors.black};
  margin-bottom: 25px;
  opacity: ${({ $hasHistory }) => ($hasHistory ? '0' : '1')};
  margin-top: ${({ $hasHistory }) => ($hasHistory ? '-100px' : '0px')};
  transition: all 0.8s ease-in-out;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 5px;
    padding: 0 20px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 5px;
  }
`;

const InputContainer = styled.div<{
  $hasChatHistory: boolean;
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
  $isDisabled: boolean;
}>`
  width: 100%;
  max-width: 800px;
  margin-bottom: ${({ $hasChatHistory }) => ($hasChatHistory ? '0px' : '20px')};
  position: relative;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.greyLight : theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  padding: 10px;
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
`;

const FormContainer = styled.div`
  width: 100%;
  padding: 0px 20px 20px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0px 10px 20px 10px;
  }
`;

const MobileFormContainer = styled.div<{
  $hasChatHistory: boolean;
  $isObjectiveReady: boolean;
}>`
  width: 100%;
  padding: 20px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;

  ${({ $hasChatHistory }) =>
    $hasChatHistory
      ? `
    position: sticky;
    left: 0;
    right: 0;
    padding: 10px 20px;
    background-color: transparent;

    `
      : `
    position: relative;
    `}

  ${({ $isObjectiveReady }) =>
    $isObjectiveReady &&
    `
    background-color: ${theme.colors.white};
    box-shadow: 0px -1.6px 3.6px rgba(0, 0, 0, 0.17);
  `}
`;

const MobileSubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  align-self: flex-end;
  margin-top: auto;
`;

const SubmitButton = styled(Button)`
  width: fit-content;
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }
`;
const SuggestionsWrapper = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SuggestionsContainer = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  max-height: ${({ $isVisible }) => ($isVisible ? '500px' : '0')};
  transition: all 0.3s linear;
  overflow: hidden;
`;

const SuggestionsGrid = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const CollapseButton = styled(Button)`
  font-size: 14px;
  font-weight: 500;
  padding: 5px 10px;
  color: ${theme.colors.greyDark};
  border: none;
  border-radius: 8px;
  background-color: transparent;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: ${theme.colors.greyVeryDark};
  }
`;

const SuggestionButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.greyVeryDark};
  padding: 8px 16px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 8px;
  background-color: rgba(251, 247, 242, 0.8);
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  min-width: 180px;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }
`;

const ShuffleButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(251, 247, 242, 0.8);
  border: 1px solid ${theme.colors.grey};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;
`;

const DropdownButton = styled(Button)`
  border: 1px solid ${theme.colors.greyLight};
  box-shadow: none;
  padding: 10px 15px;
  border-radius: 20px;
  background: none;
  height: 30px;
  display: flex;
  align-items: space-between;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;

  span {
    font-weight: 400;
  }
  svg {
    margin: 0px;
    height: 18px;
    width: 18px;
  }
`;

const LessonTypeDropdownButton = styled(DropdownButton)`
  border: 1px solid transparent;
  padding: 10px;
  height: 35px;
  border-radius: 10px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.greyLight};
    border: 1px solid ${theme.colors.grey};
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 8px;
    font-size: 14px;
    color: ${theme.colors.greyDark};

    &:hover,
    &:focus {
      background-color: ${theme.colors.grey};
    }

    span {
      font-size: 14px;
      color: ${theme.colors.greyDark};
    }

    svg {
      color: ${theme.colors.greyDark};
    }
  }
`;

const AddResourcesDropdownButton = styled(DropdownButton)`
  height: 30px;

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    border: 1px solid transparent;
  }

  svg {
    height: 15px;
    width: 15px;
    margin: 0px;
    margin-left: 5px;
  }

  .button-text {
    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    min-width: 30px;
    width: 30px;
    height: 30px;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid ${theme.colors.grey};

    &:hover {
      background-color: ${theme.colors.grey};
    }

    svg {
      margin-left: 0;
      color: ${theme.colors.greyDark};
    }
  }
`;

// Loading Overlay Component
const LoadingOverlay = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  font-size: 18px;
  font-weight: 600;
  pointer-events: none;
  color: ${theme.colors.greyDark};
`;

// Mobile Loading Overlay Component
const MobileLoadingOverlay = styled.div`
  position: absolute;
  left:0
  transform: translate(-50%, -50%);
  font-size: 16px;
  font-weight: 600;
  pointer-events: none;
  color: ${theme.colors.greyDark};
  text-align: center;
  z-index: 10;
`;

const ObjectiveRefinementComponent = () => {
  const { t } = useTranslate();
  const session = useSession();
  const { tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const isAgentHistoryEmpty = useDynamicLessonStateContext(
    c => c.dynamicLessonState.agent.agentHistory.length === 0,
  );
  const mainLessonCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.mainLessonCategory,
  );
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const generatorFilterCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.generatorFilterCategory,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const lessonIntent = useDynamicLessonStateContext(
    c => c.dynamicLessonState.lessonIntent,
  );
  const subject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);
  const currentPage = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedPage,
  );
  const isDesktop = useBreakPoint(700);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { testPrepState, dispatch } = useTestPrepState(userAccessBadge);
  const { selectedPage } = testPrepState;
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined | null>(undefined);

  useEffect(() => {
    return () => {
      if (gameAPI) {
        gameAPI.disconnect();
      }
    };
  }, [gameAPI]);

  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      // GameAPI should refresh its own tokens by itself.
      const gameAPI = new GameAPI(session.data?.idToken);
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);

  const onSelectGeneratorFilterCategory = (categoryId: string) => {
    metrics.logEvent('CreatePage.SelectCategory.Clicked', {
      selectedCategory: categoryId,
      selectedLessonCategory: mainLessonCategory,
      schoolState: presetState,
      tenantSubjects,
    });
    dispatchAndSync({
      type: 'SET_GENERATOR_FILTER_CATEGORY',
      payload: categoryId as string,
    });
  };

  const handleClearFilters = () => {
    if (mainLessonCategory === 'full_lesson') {
      if (currentPage === Page.SubjectSelection) {
        dispatchAndSync({ type: 'RESET_ALL' });
        return;
      }
      if (currentPage === Page.LessonIntentSelection) {
        dispatchAndSync({ type: 'RESET_SUBJECTS' });
        return;
      }

      if (currentPage === Page.LessonGoals) {
        // If we're in quickWrites and have a generator category selected, reset just that first
        if (lessonIntent?.id === 'quickWrites' && generatorFilterCategory && !isDesktop) {
          dispatchAndSync({ type: 'SET_GENERATOR_FILTER_CATEGORY', payload: '' });
          return;
        }
        dispatchAndSync({ type: 'RESET_LESSON_INTENT' });
        return;
      }

      if (currentPage === Page.Generate) {
        const pageToSkipTo = lessonIntent?.hasMoreDetails
          ? Page.LessonGoals
          : subject === 'advancedPrompting'
          ? Page.SubjectSelection
          : Page.LessonIntentSelection;
        dispatchAndSync({ type: 'SET_PAGE', payload: pageToSkipTo });
        return;
      }
    }

    if (mainLessonCategory === 'test_prep') {
      //if testprep
      if (selectedPage === 1) {
        dispatchAndSync({ type: 'RESET_ALL' });
        dispatch({ type: 'RESET' });
        return;
      }
      if (selectedPage === 2) {
        dispatch({ type: 'GO_BACK', payload: 1 });
        return;
      }
      if (selectedPage === 3) {
        dispatch({ type: 'GO_BACK', payload: 2 });
        return;
      }
      return;
    }

    dispatchAndSync({ type: 'RESET_ALL' });
    return;
  };

  if (session.status === 'loading') {
    return (
      <div>
        <T keyName="Loading..." defaultValue="Loading..." />
      </div>
    );
  }

  if (session.status === 'unauthenticated') {
    return (
      <div>
        <T
          keyName="Please sign in to access this feature."
          defaultValue="Please sign in to access this feature."
        />
      </div>
    );
  }

  if (!gameAPI) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <PageContainer $isCentered={isAgentHistoryEmpty}>
      {isAgentHistoryEmpty && (
        <CuripodAIIconContainer>
          <CuripodAIStarsSvg />
          <T keyName="Curipod AI" defaultValue="Curipod AI" />
        </CuripodAIIconContainer>
      )}
      <SocketProvider gameAPI={gameAPI}>
        <CustomIntercomButton
          icon={
            <IconWrapper height="20px" width="20px">
              <QuestionMarkSvg />
            </IconWrapper>
          }
        />
        <HideNextJsLoader />
        <BackgroundContainer>
          <MagicBackground />
        </BackgroundContainer>
        <BackButtonContainer>
          {!mainLessonCategory && isAgentHistoryEmpty ? (
            <Link href={`/${tenantId}/lessons`} passHref>
              <a>
                <BackButton
                  onClick={() => {
                    metrics.logEvent('CreatePage.GoHome.Clicked', {
                      prompt: prompt,
                      grade: grade,
                      subject: subject,
                      fileType: uploadedFile?.fileType,
                      currentPage: mainLessonCategory,
                      lessonIntent: lessonIntent?.id,
                      schoolState: presetState,
                    });
                  }}
                  type="primary"
                  icon={<HomeFilledSvg />}
                  styles={{
                    height: 42,
                    width: 110,
                  }}
                >
                  <T keyName="Home" defaultValue="Home" />
                </BackButton>
              </a>
            </Link>
          ) : !isAgentHistoryEmpty ? (
            <BackButton
              $iconHeight={30}
              $iconWidth={30}
              type="primary"
              styles={{
                padding: '10px',
                height: 42,
              }}
              notext={true}
              icon={
                <IconWrapper height={'30px'} width={'30px'}>
                  <ArrowLeftSvg style={{ marginRight: '0px' }} />
                </IconWrapper>
              }
              onClick={() => {
                dispatchAndSync({ type: 'RESET_ALL' });
              }}
            ></BackButton>
          ) : (
            <BackButton
              type="primary"
              styles={{
                padding: '10px 20px 10px 10px',
                height: 42,
                width: 110,
              }}
              onClick={() => {
                metrics.logEvent('CreatePage.GoBack.Clicked', {
                  prompt: prompt,
                  grade: grade,
                  subject: subject,
                  fileType: uploadedFile?.fileType,
                  currentPage: mainLessonCategory,
                  lessonIntent: lessonIntent?.id,
                  schoolState: presetState,
                });
                handleClearFilters();
              }}
              icon={
                <IconWrapper height={'30px'} width={'30px'}>
                  <ArrowLeftSvg />
                </IconWrapper>
              }
            >
              <T keyName="Back" defaultValue="Back" />
            </BackButton>
          )}
        </BackButtonContainer>
        {mainLessonCategory === 'test_prep' && lessonCategories['test_prep'].categories && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <TestPrepTab testPrepState={testPrepState} dispatch={dispatch} />
          </div>
        )}
        {mainLessonCategory === 'writing_skills' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            {isDesktop ? (
              <SubPageLayout
                pageTitle={t(
                  'Choose your quick write lesson',
                  'Choose your quick write lesson',
                )}
              >
                <QuickWritesContainer>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        onSelectGeneratorFilterCategory(categoryId);
                      }}
                      visibleCategories={writingSkills.categories || []}
                      activeCategoryId={generatorFilterCategory}
                    />
                    {generatorFilterCategory && (
                      <FindCategorizedLessonsTab
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    )}
                  </div>
                </QuickWritesContainer>
              </SubPageLayout>
            ) : (
              <>
                {!generatorFilterCategory ? (
                  <SubPageLayout pageTitle="Choose a Category">
                    <QuickWritesContainer>
                      <CategoryList
                        handleSelectCategory={(categoryId: string) => {
                          onSelectGeneratorFilterCategory(categoryId);
                        }}
                        visibleCategories={writingSkills.categories || []}
                        activeCategoryId={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                ) : (
                  <SubPageLayout pageTitle="Quick Writes">
                    <QuickWritesContainer>
                      <FindCategorizedLessonsTab
                        categoryTitle={
                          writingSkills.categories?.find(
                            category => category.id === generatorFilterCategory,
                          )?.title || ''
                        }
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                )}
              </>
            )}
          </div>
        )}
        {mainLessonCategory === 'upload' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <CurifyTab />
          </div>
        )}

        {mainLessonCategory === 'pick_theme' && (
          <div
            style={{
              zIndex: 1,
              position: 'relative',
              paddingTop: isDesktop ? '80px' : '20px',
              width: '100%',
              paddingBottom: '30px',
            }}
          >
            <PickTheme />
          </div>
        )}

        {!mainLessonCategory && <ObjectiveRefinementWithSocket />}
      </SocketProvider>
    </PageContainer>
  );
};

const createSlideTemplate = ({
  subtitle,
  title,
}: {
  title: string;
  subtitle: string;
}): Slide => {
  return {
    id: '1',
    type: 'slide',
    width: 1920,
    height: 1080,
    version: 1,
    elements: [
      {
        x: -14.812479066574937,
        y: 760.2757671336682,
        id: '1262618418378',
        type: 'shape',
        angle: 0,
        width: 2181,
        colors: ['#FFBE89'],
        height: 260,
        opacity: 1,
        version: 1,
        borderRadius: 0,
        shapeClipPath:
          'polygon(100% 100%, 0% 100% , 0.00% 43.44%, 1.33% 45.30%, 2.67% 46.82%, 4.00% 47.96%, 5.33% 48.69%, 6.67% 48.99%, 8.00% 48.86%, 9.33% 48.31%, 10.67% 47.34%, 12.00% 45.98%, 13.33% 44.25%, 14.67% 42.20%, 16.00% 39.88%, 17.33% 37.33%, 18.67% 34.61%, 20.00% 31.79%, 21.33% 28.93%, 22.67% 26.09%, 24.00% 23.34%, 25.33% 20.74%, 26.67% 18.35%, 28.00% 16.23%, 29.33% 14.42%, 30.67% 12.96%, 32.00% 11.89%, 33.33% 11.23%, 34.67% 11.00%, 36.00% 11.20%, 37.33% 11.82%, 38.67% 12.86%, 40.00% 14.29%, 41.33% 16.07%, 42.67% 18.17%, 44.00% 20.53%, 45.33% 23.12%, 46.67% 25.86%, 48.00% 28.69%, 49.33% 31.55%, 50.67% 34.38%, 52.00% 37.11%, 53.33% 39.67%, 54.67% 42.02%, 56.00% 44.09%, 57.33% 45.85%, 58.67% 47.24%, 60.00% 48.25%, 61.33% 48.83%, 62.67% 49.00%, 64.00% 48.73%, 65.33% 48.03%, 66.67% 46.93%, 68.00% 45.44%, 69.33% 43.60%, 70.67% 41.46%, 72.00% 39.05%, 73.33% 36.44%, 74.67% 33.68%, 76.00% 30.84%, 77.33% 27.97%, 78.67% 25.16%, 80.00% 22.45%, 81.33% 19.92%, 82.67% 17.62%, 84.00% 15.59%, 85.33% 13.89%, 86.67% 12.56%, 88.00% 11.63%, 89.33% 11.11%, 90.67% 11.02%, 92.00% 11.36%, 93.33% 12.12%, 94.67% 13.29%, 96.00% 14.84%, 97.33% 16.73%, 98.67% 18.93%, 100.00% 21.37%);',
        lockedPosition: true,
        lockedFromDeletion: false,
      },
      {
        x: -11.35483576657493,
        y: 870.2258631336682,
        id: '430986043544',
        type: 'shape',
        angle: 0,
        width: 2207,
        colors: ['#F99746'],
        height: 260,
        opacity: 1,
        version: 1,
        borderRadius: 0,
        shapeClipPath:
          'polygon(100% 100%, 0% 100% , 0.00% 43.44%, 1.33% 45.30%, 2.67% 46.82%, 4.00% 47.96%, 5.33% 48.69%, 6.67% 48.99%, 8.00% 48.86%, 9.33% 48.31%, 10.67% 47.34%, 12.00% 45.98%, 13.33% 44.25%, 14.67% 42.20%, 16.00% 39.88%, 17.33% 37.33%, 18.67% 34.61%, 20.00% 31.79%, 21.33% 28.93%, 22.67% 26.09%, 24.00% 23.34%, 25.33% 20.74%, 26.67% 18.35%, 28.00% 16.23%, 29.33% 14.42%, 30.67% 12.96%, 32.00% 11.89%, 33.33% 11.23%, 34.67% 11.00%, 36.00% 11.20%, 37.33% 11.82%, 38.67% 12.86%, 40.00% 14.29%, 41.33% 16.07%, 42.67% 18.17%, 44.00% 20.53%, 45.33% 23.12%, 46.67% 25.86%, 48.00% 28.69%, 49.33% 31.55%, 50.67% 34.38%, 52.00% 37.11%, 53.33% 39.67%, 54.67% 42.02%, 56.00% 44.09%, 57.33% 45.85%, 58.67% 47.24%, 60.00% 48.25%, 61.33% 48.83%, 62.67% 49.00%, 64.00% 48.73%, 65.33% 48.03%, 66.67% 46.93%, 68.00% 45.44%, 69.33% 43.60%, 70.67% 41.46%, 72.00% 39.05%, 73.33% 36.44%, 74.67% 33.68%, 76.00% 30.84%, 77.33% 27.97%, 78.67% 25.16%, 80.00% 22.45%, 81.33% 19.92%, 82.67% 17.62%, 84.00% 15.59%, 85.33% 13.89%, 86.67% 12.56%, 88.00% 11.63%, 89.33% 11.11%, 90.67% 11.02%, 92.00% 11.36%, 93.33% 12.12%, 94.67% 13.29%, 96.00% 14.84%, 97.33% 16.73%, 98.67% 18.93%, 100.00% 21.37%);',
        lockedPosition: true,
        lockedFromDeletion: false,
      },
      {
        x: 180,
        y: 180,
        id: '53583619541',
        type: 'text',
        angle: 0,
        scale: 1,
        width: 1728,
        height: 227.859375,
        opacity: 1,
        version: 1,
        textValue: JSON.stringify([
          {
            align: 'left',
            type: 'paragraph',
            size: 145,
            children: [{ text: `${title}`, bold: true, color: '#282F33' }],
          },
        ]),
        borderRadius: 0,
        lockedPosition: false,
        lockedFromDeletion: false,
      },
      {
        x: 90.9174983,
        y: 373.4856136000001,
        id: '1420470339941',
        type: 'text',
        angle: 0,
        scale: 1,
        width: 1189,
        height: 180.71875,
        opacity: 1,
        version: 1,
        textValue: JSON.stringify([
          {
            align: 'left',
            color: '#282F33',
            type: 'paragraph',
            size: 115,
            children: [{ text: subtitle, color: '#282F33' }],
          },
        ]),
        borderRadius: 0,
        lockedPosition: false,
        lockedFromDeletion: false,
      },
    ],
    backgroundMedia: {
      fileId: '#FFF8F0',
      brightness: 0.5,
      fileMimeType: 'image/color',
    },
    contextElements: [],
    durationSeconds: null,
    dataReferenceSlideId: null,
  };
};

export const COLOR_PALETTES = {
  // Default color palette (from the default theme)
  DEFAULT: {
    dark: '#F99746',
    base: '#FFBE89',
    light: '#FFEBCC',
    veryLight: '#FFF8F0',
    fontColor: '#282F33',
    backgroundColor: '#FFF8F0',
  },

  // ELA color palettes
  ELA_READING: {
    dark: '#D84C42',
    base: '#EC7070',
    light: '#F8C9C9',
    veryLight: '#FBE9E9',
    fontColor: '#282F33',
    backgroundColor: '#FBE9E9',
  },

  ELA_WRITING: {
    dark: '#2598DA',
    base: '#69B9E6',
    light: '#B3DAF5',
    veryLight: '#D6EBFA',
    fontColor: '#282F33',
    backgroundColor: '#D6EBFA',
  },

  // Math color palette
  MATH: {
    dark: '#2598DA',
    base: '#69B9E6',
    light: '#B3DAF5',
    veryLight: '#D6EBFA',
    fontColor: '#282F33',
    backgroundColor: '#D6EBFA',
  },

  // Science color palette
  SCIENCE: {
    dark: '#61B195',
    base: '#93C8B6',
    light: '#DAECE6',
    veryLight: '#EEF7F3',
    fontColor: '#282F33',
    backgroundColor: '#EEF7F3',
  },

  // Social Studies color palette
  SOCIAL_STUDIES: {
    dark: '#A575FF',
    base: '#D0B7FF',
    light: '#E8DBFF',
    veryLight: '#F1EBFF',
    fontColor: '#282F33',
    backgroundColor: '#F1EBFF',
  },
  HALLOWEEN: {
    dark: '#632400', // Deep blood red / near-black
    base: '#cd4902', // Dark brownish red (aged wood, haunted house)
    light: '#cd4902', // Pumpkin orange glow
    veryLight: '#FFF3E0', // Warm candlelight ivory
    fontColor: '#F5E6D3', // Pale bone white for text
    backgroundColor: '#0A0908', // Almost-black — night sky / shadow
  },
  SIMPLE: {
    dark: '#ffffff', // Deep golden yellow — accent / buttons
    base: '#ffffff', // Primary bright yellow
    light: '#ffffff', // Soft pastel yellow for highlights
    veryLight: '#ffffff', // Background wash, gentle and warm
    fontColor: '#282F33', // Dark gray for strong contrast
    backgroundColor: '#ffffff', // Matches veryLight for harmony
  },

  DARK_PURPLE: {
    dark: '#6C4BB3', // Deep purple-black — main background / navbar
    base: '#7e5acb', // Charcoal with a hint of violet
    light: '#9476d4', // Medium purple accent (buttons, highlights)
    veryLight: '#E5D9FF', // Soft lavender for light backgrounds or hover
    fontColor: '#F2F0FF', // Nearly white with a lilac tint
    backgroundColor: '#0B0911', // Almost black — overall background
  },

  // Other subjects color palette
  OTHER: {
    dark: '#F99746',
    base: '#FFBE89',
    light: '#FFEBCC',
    veryLight: '#FFF8F0',
    fontColor: '#282F33',
    backgroundColor: '#FFF8F0',
  },
};

const PickTheme = () => {
  const presentationId = useDynamicLessonStateContext(
    context => context.dynamicLessonState.presentationId,
  );
  const metrics = useMetrics();
  const { t } = useTranslate();

  const themedSlides: { slide: Slide; colorPalette: ColorPalette }[] = useMemo(
    () => [
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Classic Curipod', 'Classic Curipod'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.DEFAULT,
        })[0],
        colorPalette: COLOR_PALETTES.DEFAULT,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Bold & Bright', 'Bold & Bright'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.SOCIAL_STUDIES,
        })[0],
        colorPalette: COLOR_PALETTES.SOCIAL_STUDIES,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Cool & Calm', 'Cool & Calm'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.MATH,
        })[0],
        colorPalette: COLOR_PALETTES.MATH,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Fresh & Friendly', 'Fresh & Friendly'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.SCIENCE,
        })[0],
        colorPalette: COLOR_PALETTES.SCIENCE,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Big Energy', 'Big Energy'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.ELA_READING,
        })[0],
        colorPalette: COLOR_PALETTES.ELA_READING,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Simple', 'Simple'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.SIMPLE,
        })[0],
        colorPalette: COLOR_PALETTES.SIMPLE,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Chalkboard', 'Chalkboard'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.DARK_PURPLE,
        })[0],
        colorPalette: COLOR_PALETTES.DARK_PURPLE,
      },
      {
        slide: themeSlides({
          slides: [
            createSlideTemplate({
              title: t('Spooky Look', 'Spooky Look'),
              subtitle: '',
            }),
          ],
          fromColorPalette: COLOR_PALETTES.DEFAULT,
          toColorPalette: COLOR_PALETTES.HALLOWEEN,
        })[0],
        colorPalette: COLOR_PALETTES.HALLOWEEN,
      },
    ],
    [t],
  );

  const [isHovering, setIsHovering] = useState<ColorPalette | null>(null);
  const slideGridRef = useRef<HTMLDivElement>(null);

  const { tenantId, router } = useRouterMatch();
  const { gameAPI } = useContext(SocketContext);

  const [selectedTheme, setSelectedTheme] = useState<ColorPalette>(
    COLOR_PALETTES.DEFAULT,
  );

  const handleSelectTheme = (theme: ColorPalette) => {
    setSelectedTheme(theme);
  };

  const handleNext = () => {
    if (presentationId && gameAPI && selectedTheme) {
      metrics.logEvent('PickTheme', {
        theme: selectedTheme,
      });
      // Lesson generation has already started.
      // This step updates the color palette.
      // Then we redirect to the designer and listen to the generation
      gameAPI.getSocket().emit(
        'build:lesson:color-palette-changed',
        {
          presentationId,
          colorPalette: selectedTheme,
        },
        (_: {
          status: number;
          data: { colorPalette: ColorPalette };
          error?: { status: number; message: string };
        }) => {
          // Wait until the server acks before we redirect so that
          // disconnects are avoided
          router.replace(
            `/${tenantId}/lessons/${presentationId}/edit?buildInitialLesson=true`,
          );
        },
      );
    }
  };

  return (
    <PickThemeContainer>
      <Title $hasHistory={false} style={{ textAlign: 'center' }}>
        <T
          key="Final step: Select a Theme for Your Lesson"
          keyName="Final step: Select a Theme for Your Lesson"
          defaultValue="Final step: Select a Theme for Your Lesson"
        />
      </Title>
      <SlideGrid ref={slideGridRef}>
        {themedSlides.map((themedSlide, index) => (
          <SlideGridItem
            key={index}
            $isSelected={selectedTheme === themedSlide.colorPalette}
            $isHovering={isHovering === themedSlide.colorPalette}
            onMouseEnter={() => setIsHovering(themedSlide.colorPalette)}
            onMouseLeave={() => setIsHovering(null)}
            onClick={() => handleSelectTheme(themedSlide.colorPalette)}
          >
            <CSSSlideWrapperViewer
              aspect={{ height: 1080, width: 1920 }}
              hideLogo={true}
              slide={themedSlide.slide}
              backgroundMedia={themedSlide.slide?.backgroundMedia}
              resultView={false}
              showStaticPoll={false}
              isDiscoverTemplate={false}
              isOwner={true}
              lessonAttachments={[]}
            />
          </SlideGridItem>
        ))}
      </SlideGrid>
      <div style={{ display: 'grid', justifyContent: 'center' }}>
        <GenerateLessonButton
          onClick={handleNext}
          disabled={!selectedTheme}
          loading={false}
          styles={{
            width: '200px',
            height: '50px',
            borderRadius: '25px',
          }}
        >
          <T key="Continue" keyName="Continue" defaultValue="Continue" />
        </GenerateLessonButton>
      </div>
    </PickThemeContainer>
  );
};

const PickThemeContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 10%;
  margin: 0 auto;
  position: relative;
`;

const SlideGridItem = styled.div<{ $isSelected: boolean; $isHovering: boolean }>`
  aspect-ratio: 1920/1080;
  width: 100%;
  background-color: ${theme.colors.creatorBackgroundBeige};
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  outline-style: solid;
  outline-width: 4px;
  outline-color: transparent;
  transition: all 0.2s ease;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  ${({ $isSelected }) => {
    if ($isSelected) {
      return css`
        outline: 4px solid ${theme.colors.blue};
        scale: 1.01;
      `;
    }
  }}
  ${({ $isHovering }) => {
    if ($isHovering) {
      return css`
        outline: 4px solid ${theme.colors.blue};
        scale: 1.01;
      `;
    }
  }}
`;

const SlideGrid = styled.div`
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 1fr);

  max-width: 1000px;

  @media (max-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 550px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 350px) {
    grid-template-columns: repeat(1, 1fr);
    max-height: 520px;
    overflow-y: auto;
    box-shadow: ${theme.effects.defaultShadow} inset;
    padding: 10px;
    border-radius: 10px;
  }

  margin-bottom: 30px;
  justify-content: center;
  gap: 10px;
`;

const ObjectiveRefinement = () => {
  return <ObjectiveRefinementComponent />;
};

export default ObjectiveRefinement;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  svg {
    height: 98dvh; // 99 and 100 adds a scroll...
    width: 100dvw;
  }
`;
const BackButton = styled(Button)<{ $iconHeight?: number; $iconWidth?: number }>`
  padding: 10px 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.magicFloatingShadow};
  color: ${theme.colors.black};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }

  svg {
    height: ${({ $iconHeight }) => $iconHeight ?? '16'}px;
    width: ${({ $iconWidth }) => $iconWidth ?? '16'}px;
  }
`;
const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  margin-left: 20px;
  width: fit-content;
  z-index: 3;
`;
const PageContainer = styled.div<{ $isCentered?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  background: #ffc698;
  background: linear-gradient(
    180deg,
    rgb(255, 245, 238) 0%,
    rgba(255, 198, 152, 0.8) 25%,
    rgba(255, 198, 152, 0.8) 50%,
    rgb(255, 245, 238) 100%
  );
`;
const CuripodAIIconContainer = styled.div`
  position: absolute;
  cursor: default;
  top: 20px;
  right: 20px;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
  color: ${theme.colors.orange};
  svg {
    width: 100%;
    height: auto;
  }
`;

const QuickWritesContainer = styled.div`
  margin: 0 auto;
  width: 80%;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const StyledMenuItem = styled(Menu.Item)`
  & > span {
    display: grid !important;
    grid-template-columns: 24px 1fr;
    gap: 12px;
    align-items: center;

    &:hover {
      background-color: ${theme.colors.greyLight} !important;
    }

    .ant-dropdown-menu-item-icon {
      margin-right: 0 !important;
    }
  }
`;
